import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { ApiError } from "@/api/client";
import { formatRelative } from "@/lib/format";
import { CallButton } from "@/telephony/CallButton";
import type { Agent, Lead } from "./queries";
import { sourcesOf, useTriage } from "./queries";

/**
 * One enquiry, and the decision it is waiting on.
 *
 * A card rather than a table row because the decision needs everything at
 * once: who wrote in, through which channel, how long ago, and whether there
 * is enough to convert them. A table that hides half of that behind a detail
 * page turns one decision into two screens.
 */
export function LeadCard({
  lead,
  agents,
  canTriage,
}: {
  lead: Lead;
  agents: Agent[];
  /** Mirrors `IsTriageRole`. Rendering the form without it earns a 403. */
  canTriage: boolean;
}) {
  const { t } = useTranslation();
  const [agent, setAgent] = useState("");
  const triage = useTriage();

  const sources = sourcesOf(lead);
  const contactable = lead.phone || lead.wa_id;

  return (
    <li className="border-2 border-rule bg-panel p-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="font-medium">{lead.company_name}</h3>
        <span className="tag tag-neutral">
          {t(`leads.channel.${lead.channel ?? 1}`)}
        </span>
        <span className="ml-auto text-xs text-mute-600">
          {formatRelative(lead.created)}
        </span>
      </div>

      <dl className="mt-2 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <div className="flex gap-2">
          <dt className="text-mute-600">{t("leads.contact")}</dt>
          <dd>{lead.contact_name || "—"}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-mute-600">{t("leads.phone")}</dt>
          <dd>{lead.phone || lead.wa_id || "—"}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-mute-600">{t("leads.email")}</dt>
          <dd className="break-all">{lead.email || "—"}</dd>
        </div>
        {sources.length > 0 ? (
          <div className="flex gap-2">
            <dt className="text-mute-600">{t("leads.source")}</dt>
            {/* Several can be true at once — someone can arrive from a map and
                a referral — so this is a list, not a value. */}
            <dd>{sources.join(", ")}</dd>
          </div>
        ) : null}
      </dl>

      {!lead.has_enough_data ? (
        <p className="mt-2 text-xs text-brand-700">{t("leads.incomplete")}</p>
      ) : null}

      {triage.isError ? (
        <p role="alert" className="mt-2 text-xs text-brand-700">
          {triage.error instanceof ApiError
            ? triage.error.message
            : t("leads.triageFailed")}
        </p>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {contactable ? (
          <CallButton number={contactable} label={lead.company_name} />
        ) : null}

        {lead.current_agent === null && canTriage ? (
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (agent) triage.mutate({ id: lead.id, agent: Number(agent) });
            }}
          >
            <label className="sr-only" htmlFor={`agent-${lead.id}`}>
              {t("leads.assignTo")}
            </label>
            <select
              id={`agent-${lead.id}`}
              value={agent}
              onChange={(event) => setAgent(event.target.value)}
              className="input w-auto py-1.5 text-sm"
            >
              <option value="">{t("leads.pickAgent")}</option>
              {agents.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={!agent || triage.isPending}
              className="btn btn-primary px-3 py-1.5 text-sm"
            >
              {t("leads.triage")}
            </button>
          </form>
        ) : lead.current_agent !== null ? (
          <span className="text-sm text-mute-600">{t("leads.alreadyTriaged")}</span>
        ) : null}

        {lead.company !== null ? (
          <Link className="text-sm underline" to={`/prospectos/${lead.company}`}>
            {t("leads.openCompany")}
          </Link>
        ) : null}
      </div>
    </li>
  );
}
